import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, collections } from '@/lib/firebase';
import { toast } from 'react-hot-toast';

const evaluatorsDetailData = {
  __metadata: {
    immutableFields: [
      'evaluators',
      'evaluators.*.name',
      'evaluators.*.role',
      'evaluators.*.sections',
      'evaluators.*.sections.*.title',
      'evaluators.*.sections.*.content'
    ],
    version: '1.0',
    lastUpdated: 'auto-generated'
  },
  evaluators: [
    {
      name: "Sophia Carter",
      role: "Peer",
      sections: [
        {
          title: "Communication and Collaboration Strategies",
          content: [
            {
              subtitle: "Conversation Starter",
              text: "How do you think we could improve how we collaborate on cross-functional projects?"
            },
            {
              subtitle: "Tip",
              text: "Sophia prefers concise and actionable updates during meetings; prepare clear bullet points for discussions."
            },
            {
              subtitle: "Listening Practice",
              text: "Repeat back Sophia's key points to confirm understanding and show engagement."
            }
          ]
        },
        {
          title: "Building Trust and Strengthening Relationships",
          content: [
            {
              subtitle: "Empathy Builder",
              text: "Consider Sophia's workload during project deadlines and offer support when appropriate."
            },
            {
              subtitle: "Trust Strategy",
              text: "Deliver on promises, such as meeting deadlines for shared tasks."
            },
            {
              subtitle: "Recognition Practice",
              text: "Publicly acknowledge Sophia's contributions during team retrospectives."
            }
          ]
        },
        {
          title: "Constructive Feedback and Conflict Resolution",
          content: [
            {
              subtitle: "Feedback Request",
              text: "What's one thing I could adjust to make our collaboration smoother?"
            },
            {
              subtitle: "Conflict Technique",
              text: "Address disagreements constructively, e.g., \"Let's explore a middle ground for our project timeline.\""
            },
            {
              subtitle: "Reflective Prompt",
              text: "Did I effectively address Sophia's concerns during our last meeting?"
            }
          ]
        },
        {
          title: "Leveraging Strengths and Shared Goals",
          content: [
            {
              subtitle: "Shared Goal",
              text: "Collaborate to streamline project workflows."
            },
            {
              subtitle: "Strength Amplifier",
              text: "Sophia's organizational skills can help in creating structured project plans."
            },
            {
              subtitle: "Balance Focus",
              text: "Encourage Sophia to pair strategic planning with actionable next steps."
            }
          ]
        }
      ]
    },
    {
      name: "Liam Johnson",
      role: "Line Manager",
      sections: [
        {
          title: "Communication and Collaboration Strategies",
          content: [
            {
              subtitle: "Conversation Starter",
              text: "Are there ways I could make updates more efficient for you?"
            },
            {
              subtitle: "Tip",
              text: "Liam values proactive updates; send a brief summary of progress before formal reviews."
            },
            {
              subtitle: "Listening Practice",
              text: "Take notes during discussions and follow up on action items."
            }
          ]
        },
        {
          title: "Building Trust and Strengthening Relationships",
          content: [
            {
              subtitle: "Empathy Builder",
              text: "Show understanding of Liam's strategic vision by aligning your efforts with organizational priorities."
            },
            {
              subtitle: "Trust Strategy",
              text: "Be transparent about challenges and provide realistic solutions during check-ins."
            },
            {
              subtitle: "Recognition Practice",
              text: "Thank Liam for mentoring opportunities, e.g., \"Your feedback during my presentation preparation was invaluable.\""
            }
          ]
        },
        {
          title: "Constructive Feedback and Conflict Resolution",
          content: [
            {
              subtitle: "Feedback Request",
              text: "What's one skill you think I should develop to contribute more effectively to the team?"
            },
            {
              subtitle: "Conflict Technique",
              text: "Approach disagreements by focusing on shared organizational objectives."
            },
            {
              subtitle: "Reflective Prompt",
              text: "Am I presenting my ideas in a way that aligns with Liam's expectations?"
            }
          ]
        },
        {
          title: "Leveraging Strengths and Shared Goals",
          content: [
            {
              subtitle: "Shared Goal",
              text: "Support the department's strategic initiatives through improved project outcomes."
            },
            {
              subtitle: "Strength Amplifier",
              text: "Liam's big-picture thinking can inspire innovative approaches to challenges."
            },
            {
              subtitle: "Balance Focus",
              text: "Translate Liam's strategic vision into actionable steps for your team."
            }
          ]
        }
      ]
    },
    {
      name: "Ava Martinez",
      role: "Direct Report",
      sections: [
        {
          title: "Communication and Collaboration Strategies",
          content: [
            {
              subtitle: "Conversation Starter",
              text: "How can I support you better in achieving your goals?"
            },
            {
              subtitle: "Tip",
              text: "Ava appreciates regular check-ins; schedule bi-weekly one-on-one meetings to discuss her progress."
            },
            {
              subtitle: "Listening Practice",
              text: "Create a safe space for Ava to share challenges by actively listening and asking open-ended questions."
            }
          ]
        },
        {
          title: "Building Trust and Strengthening Relationships",
          content: [
            {
              subtitle: "Empathy Builder",
              text: "Understand Ava's career aspirations and provide growth opportunities."
            },
            {
              subtitle: "Trust Strategy",
              text: "Delegate tasks with clear expectations and provide constructive feedback."
            },
            {
              subtitle: "Recognition Practice",
              text: "Highlight Ava's contributions during team meetings, e.g., \"Ava's creativity was key to this project's success.\""
            }
          ]
        },
        {
          title: "Constructive Feedback and Conflict Resolution",
          content: [
            {
              subtitle: "Feedback Request",
              text: "Is there anything I could do differently to better support your work?"
            },
            {
              subtitle: "Conflict Technique",
              text: "Address misunderstandings promptly and focus on resolving issues collaboratively."
            },
            {
              subtitle: "Reflective Prompt",
              text: "Am I providing Ava with enough guidance and support without micromanaging?"
            }
          ]
        },
        {
          title: "Leveraging Strengths and Shared Goals",
          content: [
            {
              subtitle: "Shared Goal",
              text: "Ensure project deadlines are met while fostering Ava's professional development."
            },
            {
              subtitle: "Strength Amplifier",
              text: "Ava's creativity can be leveraged for brainstorming innovative solutions."
            },
            {
              subtitle: "Balance Focus",
              text: "Help Ava align her individual goals with the team's objectives."
            }
          ]
        }
      ]
    }
  ]
};

const evaluatorsCoherenceData = {
  __metadata: {
    immutableFields: [
      'score',
      'description'
    ],
    version: '1.0',
    lastUpdated: 'auto-generated'
  },
  score: 87,
  description: 'High consistency among evaluators\' responses, indicating reliable feedback'
};

const evaluatorsSentimentData = {
  __metadata: {
    immutableFields: [
      'score',
      'description'
    ],
    version: '1.0',
    lastUpdated: 'auto-generated'
  },
  score: 35,
  description: 'Overall positive sentiment from evaluators\' feedback'
};

const softHardSkillsData = {
  __metadata: {
    immutableFields: [
      'introText',
      'softSkills',
      'hardSkills',
      'detailedFeedback',
      'actionableRecommendations'
    ],
    version: '1.0',
    lastUpdated: 'auto-generated'
  },
  introText: "Michael, your technical expertise and problem-solving abilities stand out as key strengths, showcasing your capacity to deliver high-quality results in challenging situations. These hard skills set you apart as a dependable professional. On the other hand, your leadership and communication skills represent areas for growth. At times, your assertive tone may come across as pushy, and enhancing your ability to inspire and motivate others will elevate your leadership impact. By refining these interpersonal skills, you'll unlock your full potential to foster stronger connections, lead more effectively, and create a balanced professional profile of technical and relational excellence.",
  softSkills: {
    selfScore: 3.5,
    evaluatorScore: 2.8
  },
  hardSkills: {
    selfScore: 4.0,
    evaluatorScore: 4.2
  },
  detailedFeedback: [
    "Your hard skills, particularly technical expertise and problem-solving, are standout strengths. You consistently deliver high-quality results and effectively handle complex challenges. This establishes you as a dependable and skilled professional.",
    "Soft skills, while adequate in some areas, have room for development to balance your technical strengths. Evaluators noted opportunities to refine interpersonal dynamics, inspire your team more effectively, and ensure a collaborative tone in your interactions."
  ],
  actionableRecommendations: [
    "Continue leveraging your technical strengths by mentoring peers or sharing best practices to enhance team performance.",
    "Actively involve others in problem-solving processes to foster collaborative solutions and diverse perspectives.",
    "Focus on building rapport and strengthening relationships by fostering empathy and active listening in conversations.",
    "Participate in training sessions or workshops that enhance interpersonal dynamics, emotional intelligence, and team motivation.",
    "Develop strategies to balance assertiveness with inclusivity in communication and leadership scenarios."
  ]
};

const skillsDetailData = {
  __metadata: {
    immutableFields: [
      'dimensions',
      'dimension.name',
      'dimension.tag',
      'dimension.summary',
      'dimension.selfScore',
      'dimension.evaluatorScore',
      'dimension.feedback',
      'dimension.specificExamples',
      'dimension.actionableRecommendations'
    ],
    version: '1.0',
    lastUpdated: 'auto-generated'
  },
  dimensions: [
    {
      name: "Communication",
      tag: "Blind Spot",
      summary: "Your communication is clear and effective, but evaluators noted occasional assertiveness that may come across as pushy. Refining your tone and delivery could enhance message receptiveness.",
      selfScore: 3.5,
      evaluatorScore: 2.0,
      feedback: [
        "Your clarity in presenting ideas is highly appreciated.",
        "Evaluators suggest toning down assertiveness for smoother interactions.",
        "A focus on active listening can further improve outcomes."
      ],
      specificExamples: "Evaluators noted that your communication often appears clear and goal-oriented. However, a few instances highlighted a perceived lack of openness to feedback, which could hinder collaboration.",
      actionableRecommendations: [
        "Work on creating two-way communication channels by actively asking for input and feedback during discussions.",
        "Engage in active listening exercises to balance assertiveness with inclusivity.",
        "Consider communication coaching sessions to refine tone and messaging, ensuring clarity without being perceived as overly assertive."
      ]
    },
    {
      name: "Collaboration",
      tag: "Key Strength",
      summary: "You are an excellent team player who fosters collaboration and harmony. Peers value your approachability and ability to adapt to group dynamics.",
      selfScore: 4.5,
      evaluatorScore: 4.6,
      feedback: [
        "Peers feel supported by your inclusive approach.",
        "Your adaptability in group tasks is highly valued.",
        "Continue to foster open communication within teams for maximum impact."
      ],
      specificExamples: "Your ability to foster collaboration is highly appreciated, as seen in a recent cross-departmental project where you encouraged all team members to contribute ideas. Peers highlighted your approachability and adaptability when resolving inter-team challenges.",
      actionableRecommendations: [
        "Continue building strong relationships by regularly seeking input from quieter team members to ensure inclusivity.",
        "Take the lead in organizing team-building activities to further enhance rapport among colleagues.",
        "Leverage collaboration tools to streamline communication and task management for group projects."
      ]
    },
    {
      name: "Initiative",
      tag: "Hidden Strength",
      summary: "Your proactive nature and willingness to take charge are seen as strengths. While evaluators align with your view, focusing on balancing ambition with team dynamics can be beneficial.",
      selfScore: 3.0,
      evaluatorScore: 4.0,
      feedback: [
        "Your initiative drives projects forward efficiently.",
        "Some evaluators suggest ensuring alignment with team priorities.",
        "Leverage your initiative to inspire colleagues further."
      ],
      specificExamples: "Your proactive approach was evident during a recent project where you stepped up to manage a potential risk. However, evaluators suggest balancing proactivity with seeking input to avoid overwhelming team dynamics.",
      actionableRecommendations: [
        "Set clear boundaries for your initiatives to ensure alignment with team objectives and priorities.",
        "Take a coaching or mentoring role to inspire colleagues and promote shared ownership of proactive decisions.",
        "Reflect on past actions to assess where collaboration could have been further integrated into your initiatives."
      ]
    },
    {
      name: "Problem-Solving",
      tag: "",
      summary: "Problem-solving is one of your standout strengths, with high scores across both self-assessment and evaluators. Your analytical approach and decisiveness are admired.",
      selfScore: 4.6,
      evaluatorScore: 4.5,
      feedback: [
        "Your ability to analyze and resolve complex issues is highly respected.",
        "Peers appreciate your decisive action during challenges.",
        "Enhancing collaboration during problem-solving can create broader solutions."
      ],
      specificExamples: "Evaluators commended your analytical skills, especially during high-stakes situations like resolving technical bottlenecks in client presentations. However, there's an opportunity to further involve your team in brainstorming solutions for more comprehensive outcomes.",
      actionableRecommendations: [
        "Facilitate problem-solving workshops to encourage collective team input and share your decision-making process.",
        "Document successful resolutions and share lessons learned as best practices for future challenges.",
        "Attend advanced problem-solving or strategic thinking courses to further refine your approach."
      ]
    },
    {
      name: "Leadership",
      tag: "Blind Spot",
      summary: "Leadership development remains an area for growth. Evaluators note a need to inspire and motivate team members, aligning with your own recognition of this opportunity.",
      selfScore: 3.0,
      evaluatorScore: 1.8,
      feedback: [
        "Peers feel your leadership can be more empowering.",
        "Focus on fostering a motivational environment for team members.",
        "Develop strategies to align team goals with broader organizational objectives."
      ],
      specificExamples: "Leadership emerged as an area for improvement, with evaluators noting that more motivational engagement with your team is needed. For example, team members mentioned a lack of emotional connection when goals are communicated.",
      actionableRecommendations: [
        "Focus on motivational techniques, such as public recognition of team achievements and creating a shared vision for projects.",
        "Participate in leadership development programs to enhance skills in inspiring and empowering your team.",
        "Build stronger relationships with team members through one-on-one meetings to understand their unique goals and challenges."
      ]
    },
    {
      name: "Performance",
      tag: "Confirmed Strength",
      summary: "Consistently high performance defines your professional contributions. Both you and evaluators agree on your ability to deliver high-quality results under pressure.",
      selfScore: 4.7,
      evaluatorScore: 4.8,
      feedback: [
        "Your consistent delivery of results is highly valued by peers.",
        "Maintain focus on performance quality while ensuring team alignment.",
        "Consider sharing best practices to uplift overall team performance."
      ],
      specificExamples: "Both evaluators and self-assessment align in recognizing your consistent delivery of high-quality work, even under pressure. However, there were mentions of a tendency to focus excessively on perfection, potentially slowing task delegation.",
      actionableRecommendations: [
        "Develop time-management strategies to ensure timely task completion while maintaining quality.",
        "Delegate more effectively by creating clear guidelines or SOPs for repetitive tasks.",
        "Engage in performance optimization workshops to learn how to balance perfectionism with productivity."
      ]
    }
  ]
};

const radarChartData = {
  __metadata: {
    immutableFields: [
      'radarChart',
      'radarChart.*.user',
      'radarChart.*.evaluators'
    ],
    version: '1.0',
    lastUpdated: 'auto-generated'
  },
  radarChart: {
    Communication: { user: 3.5, evaluators: 2.0 },
    Collaboration: { user: 4.5, evaluators: 4.6 },
    Initiative: { user: 3.0, evaluators: 4.0 },
    "Problem-Solving": { user: 4.6, evaluators: 4.5 },
    Leadership: { user: 3.0, evaluators: 1.8 },
    Performance: { user: 4.7, evaluators: 4.8 }
  }
};

export default function ReportDataTester() {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleGenerateReport = async () => {
    if (!currentUser) {
      toast.error('User must be logged in');
      return;
    }
    
    setLoading(true);

    try {
      const reportRef = doc(db, collections.reports, currentUser.uid);
      
      // Add GROW framework data
      await setDoc(reportRef, {
        grow_view_support_framework: JSON.stringify({
          __metadata: {
            immutableFields: [
              'goals',
              'goals.*.title',
              'goals.*.description',
              'goals.*.items',
              'reality',
              'reality.*.title',
              'reality.*.description',
              'reality.*.items',
              'options',
              'options.*.title',
              'options.*.description',
              'options.*.items',
              'wayForward',
              'wayForward.shortTerm',
              'wayForward.shortTerm.*.title',
              'wayForward.shortTerm.*.description',
              'wayForward.longTerm',
              'wayForward.longTerm.*.title',
              'wayForward.longTerm.*.description'
            ],
            version: '1.0',
            lastUpdated: 'auto-generated'
          },
          goals: [
            {
              title: "Enhance leadership presence and team motivation skills",
              description: "Based on your feedback, focus on developing a more impactful leadership style",
              items: [
                "Improve team motivation techniques",
                "Develop more effective communication strategies",
                "Balance technical excellence with interpersonal effectiveness"
              ]
            }
          ],
          reality: [
            {
              title: "Current Situation Assessment",
              description: "Analysis of your present leadership and team dynamics",
              items: [
                "Strong technical skills but room for growth in soft skills",
                "Communication style sometimes perceived as too assertive",
                "Team values your expertise but seeks more emotional engagement"
              ]
            }
          ],
          options: [
            {
              title: "Development Opportunities",
              description: "Available paths for professional growth",
              items: [
                "Enroll in leadership development programs",
                "Practice active listening and emotional intelligence techniques",
                "Seek mentorship opportunities for guided development"
              ]
            }
          ],
          wayForward: {
            shortTerm: [
              {
                title: "Immediate Actions",
                description: "Steps to take in the next 3 months",
                items: [
                  "Schedule weekly team check-ins focused on emotional connection",
                  "Document and share knowledge through collaborative platforms",
                  "Set monthly goals for leadership skill development"
                ]
              }
            ],
            longTerm: [
              {
                title: "Strategic Development",
                description: "Long-term growth initiatives",
                items: [
                  "Develop a comprehensive leadership development roadmap with quarterly milestones",
                  "Build a mentorship program to foster leadership skills in team members",
                  "Create a framework for measuring and tracking emotional intelligence growth",
                  "Establish a personal brand development strategy aligned with leadership goals",
                  "Design and implement a team culture transformation initiative"
                ]
              }
            ]
          },
          generatedAt: serverTimestamp()
        }),
        grow_view_support_resources: JSON.stringify({
          __metadata: {
            immutableFields: [
              'keyConcepts',
              'keyConcepts.*.title',
              'keyConcepts.*.description',
              'keyConcepts.*.url',
              'recommendedBooks',
              'recommendedBooks.*.title',
              'recommendedBooks.*.description',
              'recommendedBooks.*.url',
              'tedTalks',
              'tedTalks.*.title',
              'tedTalks.*.description',
              'tedTalks.*.url',
              'podcasts',
              'podcasts.*.title',
              'podcasts.*.description',
              'podcasts.*.url'
            ],
            version: '1.0',
            lastUpdated: 'auto-generated'
          },
          keyConcepts: [
            {
              title: "Emotional Intelligence",
              description: "Understanding and managing emotions effectively in professional contexts",
              url: "https://en.wikipedia.org/wiki/Emotional_intelligence"
            },
            {
              title: "Situational Leadership",
              description: "Adapting leadership style based on team maturity and task requirements",
              url: "https://en.wikipedia.org/wiki/Situational_leadership_theory"
            },
            {
              title: "Active Listening",
              description: "Fully concentrating, understanding, responding, and remembering in conversations",
              url: "https://en.wikipedia.org/wiki/Active_listening"
            }
          ],
          recommendedBooks: [
            {
              title: "Emotional Intelligence 2.0",
              description: "Practical strategies for improving emotional intelligence in professional settings",
              url: "https://www.amazon.com/Emotional-Intelligence-Matter-More-Than/dp/055338371X/"
            },
            {
              title: "Crucial Conversations",
              description: "Tools for handling high-stakes discussions and difficult feedback",
              url: "https://www.amazon.com/Crucial-Conversations-Talking-Stakes-Second/dp/1469266822/"
            },
            {
              title: "Leadership and Self-Deception",
              description: "Understanding how self-deception affects leadership effectiveness",
              url: "https://www.amazon.com/Leadership-Self-Deception-Getting-Out-Box/dp/1523097809/"
            }
          ],
          tedTalks: [
            {
              title: "How Great Leaders Inspire Action",
              description: "Simon Sinek explores how leaders can inspire cooperation, trust and change",
              url: "https://www.ted.com/talks/simon_sinek_how_great_leaders_inspire_action"
            },
            {
              title: "The Power of Vulnerability",
              description: "Brené Brown on how vulnerability strengthens leadership and connections",
              url: "https://www.ted.com/talks/brene_brown_the_power_of_vulnerability"
            },
            {
              title: "Your Body Language Shapes Who You Are",
              description: "Amy Cuddy reveals how body language influences leadership presence",
              url: "https://www.ted.com/talks/amy_cuddy_your_body_language_may_shape_who_you_are"
            }
          ],
          podcasts: [
            {
              title: "Coaching for Leaders",
              description: "Weekly insights on leadership development and emotional intelligence",
              url: "https://podcasts.apple.com/us/podcast/coaching-for-leaders/id458827716"
            },
            {
              title: "HBR IdeaCast",
              description: "Leading thinkers in management and leadership share their insights",
              url: "https://podcasts.apple.com/us/podcast/harvard-business-review-ideacast/id152022135"
            },
            {
              title: "Radical Candor",
              description: "Practical guidance on building better relationships at work",
              url: "https://podcasts.apple.com/us/podcast/radical-candor/id1442929179"
            }
          ],
          generatedAt: serverTimestamp()
        }),
      }, { merge: true });

      // Add evaluators detail data
      await setDoc(reportRef, {
        evaluators_view_evaluators_detail: JSON.stringify({
          ...evaluatorsDetailData,
          generatedAt: serverTimestamp(),
          __metadata: {
            ...evaluatorsDetailData.__metadata,
            lastUpdated: serverTimestamp()
          }
        })
      }, { merge: true });

      await setDoc(reportRef, {
        evaluators_view_coherence_score: JSON.stringify({
          ...evaluatorsCoherenceData,
          generatedAt: serverTimestamp(),
          __metadata: {
            ...evaluatorsCoherenceData.__metadata,
            lastUpdated: serverTimestamp()
          }
        }),
        evaluators_view_sentiment_score: JSON.stringify({
          ...evaluatorsSentimentData,
          generatedAt: serverTimestamp(),
          __metadata: {
            ...evaluatorsSentimentData.__metadata,
            lastUpdated: serverTimestamp()
          }
        }),
        results_overview_skills_detail: JSON.stringify({
          ...skillsDetailData,
          generatedAt: serverTimestamp(),
          __metadata: {
            ...skillsDetailData.__metadata,
            lastUpdated: serverTimestamp()
          }
        }),
        results_overview_radar_chart_scores: JSON.stringify({
          ...radarChartData,
          generatedAt: serverTimestamp(),
          __metadata: {
            ...radarChartData.__metadata,
            lastUpdated: serverTimestamp()
          }
        }),
        results_overview_soft_hard_skills: JSON.stringify({
          ...softHardSkillsData,
          generatedAt: serverTimestamp(),
          __metadata: {
            ...softHardSkillsData.__metadata,
            lastUpdated: serverTimestamp()
          }
        }),
        executive_summary_text: JSON.stringify({
          content: "Your feedback assessment paints a compelling picture of your strengths and areas where growth can take you even further. You're recognized as a strong performer and a reliable team player who consistently delivers high-quality results. Your adaptability stands out as a key strength, allowing you to thrive in dynamic environments and contribute effectively to team success. At the same time, there are areas where you can sharpen your skills to reach the next level. Some of your peers noted that your communication style can occasionally come across as too assertive, which might impact how your ideas are received. Additionally, while your technical and adaptive skills are exceptional, there's an opportunity to develop your leadership further. Specifically, focusing on inspiring and motivating your direct reports can help you foster a more engaged and empowered team. Overall, you're on a strong path, and with targeted improvements in communication and leadership, you have the potential to drive even greater impact and elevate your career. This feedback is a clear roadmap for continued growth and success, giving you actionable steps to refine your approach and unlock your full potential.",
          generatedAt: serverTimestamp()
        }),
        executive_summary_audio_url: JSON.stringify({
          url: "https://firebasestorage.googleapis.com/v0/b/insightalks-47e16.firebasestorage.app/o/Performance%20Review_%20Michael.mp3?alt=media&token=bd5b6f54-3d99-4c4a-a63b-f235b547269f",
          generatedAt: serverTimestamp()
        }),
        self_assessment_stress: JSON.stringify({
          __metadata: {
            immutableFields: [
              'stressLevel', 
              'reflection',
              'resilienceTraits',
              'actionableInsights'
            ],
            version: '1.0',
            lastUpdated: 'auto-generated'
          },
          stressLevel: 2.0,
          reflection: "While your current stress levels appear to be in a healthy range at 2.0/5, some of your responses indicate potential areas of concern regarding work-life balance. Your tendency to extend work hours and reduced attention to regular breaks may be contributing to occasional reactive behaviors in high-pressure situations. Consider implementing more structured rest periods and clearer boundaries between work and personal time to maintain your current healthy stress levels and prevent potential burnout.",
          resilienceTraits: [
            "Strong adaptability in dynamic work environments",
            "Quick recovery from setbacks and challenges",
            "Effective problem-solving under pressure"
          ],
          actionableInsights: [
            "Implement regular stress management techniques",
            "Establish clear work-life boundaries",
            "Practice mindfulness during high-pressure situations"
          ],
          generatedAt: serverTimestamp()
        }),
        self_assessment_disc_score: JSON.stringify({
          __metadata: {
            immutableFields: [
              'dominance',
              'influence',
              'steadiness',
              'conscientiousness'
            ],
            version: '1.0',
            lastUpdated: 'auto-generated'
          },
          dominance: 3.0,
          influence: 4.5,
          steadiness: 3.5,
          conscientiousness: 4.8,
          generatedAt: serverTimestamp()
        }),
        self_assessment_disc_detail: JSON.stringify({
          __metadata: {
            immutableFields: [
              'profileType',
              'strengths',
              'challenges',
              'preferredStyle',
              'traits'
            ],
            version: '1.0',
            lastUpdated: 'auto-generated'
          },
          profileType: 'Influential-Conscientious (I-C)',
          strengths: 'Your ability to combine charisma with meticulous planning makes you a persuasive leader who values both relationships and results.',
          challenges: 'At times, your conscientious nature might clash with the flexibility needed in collaborative scenarios.',
          preferredStyle: 'You thrive in structured environments where you can engage others while working toward clear, high-quality outcomes.',
          traits: [
            {
              title: 'Behavioral Tendencies Under Pressure',
              description: [
                'You perform well under pressure, leveraging analytical skills to resolve issues.',
                'Tendencies include increased assertiveness, which may come across as pushy.',
                'Adapting a calm and inclusive approach during crises will enhance team dynamics.'
              ]
            },
            {
              title: 'Blind Spots and Biases',
              description: [
                'You underestimate the impact of assertive communication on peers.',
                'Overestimate your ability to motivate others without active emotional engagement.',
                'Addressing these gaps will align your intentions with perceptions.'
              ]
            },
            {
              title: 'Emotional Triggers and Responses',
              description: [
                'Constructive criticism may initially trigger defensiveness but leads to reflection.',
                'Praise motivates you, but lack of recognition can impact morale.',
                'Awareness of these triggers can enhance emotional regulation and resilience.'
              ]
            },
            {
              title: 'Decision-Making Style',
              description: [
                'Logical and data-driven in decision-making, with a preference for structured approaches.',
                'Occasionally prioritize efficiency over collaboration in high-pressure decisions.',
                'Encouraging input from others can balance efficiency with inclusivity.'
              ]
            },
            {
              title: 'Feedback Responsiveness',
              description: [
                'You are generally open to feedback but may rationalize critical input initially.',
                'Emphasizing active listening will help build trust and improve acceptance of feedback.',
                'Regular feedback loops can foster continuous improvement.'
              ]
            },
            {
              title: 'Growth Mindset Indicators',
              description: [
                'You exhibit a strong desire to learn and grow, as evidenced by your openness to feedback.',
                'Challenges are viewed as opportunities, but persistence in self-improvement can be heightened.',
                'Engaging in professional development programs will support ongoing growth.'
              ]
            },
            {
              title: 'Influence and Impact',
              description: [
                'Your influence is strong due to your charisma and ability to engage others.',
                'Increased focus on empowering your team can amplify your impact further.',
                'Sharing your vision regularly will inspire and align team efforts.'
              ]
            },
            {
              title: 'Personal Energy Management',
              description: [
                'You manage energy well but risk burnout during high-intensity periods.',
                'Implementing regular breaks and balancing workloads can sustain performance.',
                'Focusing on work-life balance will enhance long-term productivity.'
              ]
            },
            {
              title: 'Alignment Between Intentions and Actions',
              description: [
                'Your intentions to collaborate and lead effectively are clear, but execution occasionally misaligns.',
                'Emotional tone and delivery in communication may unintentionally conflict with intentions.',
                'Focusing on aligning actions with values will build consistency.'
              ]
            },
            {
              title: 'Strategic Thinking vs. Execution Bias',
              description: [
                'You exhibit a slight bias toward execution, focusing on getting tasks done efficiently.',
                'Strengthening strategic thinking will ensure long-term alignment with organizational goals.',
                'Allocating time for big-picture planning can balance tactical execution.'
              ]
            },
            {
              title: 'Conflict Management Style',
              description: [
                'Assertive and solution-focused during conflicts but may overlook emotional nuances.',
                'Collaborative resolution techniques can help balance assertiveness with empathy.',
                'Developing active listening skills will strengthen conflict resolution outcomes.'
              ]
            },
            {
              title: 'Personal Energy Management',
              description: [
                'You manage energy well but risk burnout during high-intensity periods.',
                'Implementing regular breaks and balancing workloads can sustain performance.',
                'Focusing on work-life balance will enhance long-term productivity.'
              ]
            }
          ],
          generatedAt: serverTimestamp()
        }),
        self_assessment_summary: JSON.stringify({
          __metadata: {
            immutableFields: [
              'paragraphs'
            ],
            version: '1.0',
            lastUpdated: 'auto-generated'
          },
          paragraphs: [
            "Your self-assessment reveals a strong foundation in technical skills and analytical thinking, complemented by effective communication abilities. While you demonstrate confidence in your expertise, there's room for growth in leadership and emotional intelligence aspects.",
            "You show particular strength in problem-solving and project execution, consistently delivering high-quality results. Your ability to handle complex technical challenges and maintain high standards is evident in both your self-evaluation and peer feedback.",
            "Areas identified for development include enhancing your leadership presence and refining your approach to team motivation. Your self-awareness in recognizing these growth opportunities aligns with feedback from evaluators, providing a clear direction for professional development."
          ],
          generatedAt: serverTimestamp()
        })
      }, { merge: true });

      toast.success('Test report data generated successfully');
    } catch (error: any) {
      console.error('Error generating test report:', error);
      toast.error(error.message || 'Failed to generate test report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <button
        onClick={handleGenerateReport}
        disabled={loading}
        className="w-full bg-primary text-dark px-4 py-2 rounded hover:bg-primary-hover transition-colors disabled:opacity-50"
      >
        {loading ? 'Generating Test Report...' : 'Generate Test Report'}
      </button>
    </div>
  );
}